import { useEffect, useState, useMemo, useRef } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { format, addDays, startOfWeek } from "date-fns";
import { Anchor, Clock, Users, ChevronRight, CheckCircle2, CalendarDays, Ticket, Wallet, Sparkles, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Gift } from "lucide-react";
import RegistrationForm from "@/components/RegistrationForm";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Bubbles } from "@/components/Motifs";
import api, { formatApiError, uploadImage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function Book() {
  const { user, ready } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [programs, setPrograms] = useState([]);
  const [packages, setPackages] = useState([]);
  const [children, setChildren] = useState([]);
  const [credits, setCredits] = useState([]);

  const [programId, setProgramId] = useState(params.get("program") || "");
  const [childId, setChildId] = useState("");
  const [instructor, setInstructor] = useState("krystal");
  const [instructors, setInstructors] = useState([]);
  const [weekOffset, setWeekOffset] = useState(0);
  const [selDate, setSelDate] = useState("");
  const [slots, setSlots] = useState([]);
  const [selTime, setSelTime] = useState("");
  const [discount, setDiscount] = useState("");
  const [discountInfo, setDiscountInfo] = useState(null);
  const [applyingCode, setApplyingCode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [payMethod, setPayMethod] = useState("card");
  const [venmoInfo, setVenmoInfo] = useState(null);
  const [showWelcome, setShowWelcome] = useState(!!params.get("continue"));
  const [newChild, setNewChild] = useState({ name: "", age: "", photoUrl: "", uploading: false });
  const [regOpen, setRegOpen] = useState(false);
  const [otterCode, setOtterCode] = useState("");
  const [otterRem, setOtterRem] = useState(0);

  const goTo = (id) => setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" }), 90);

  useEffect(() => {
    api.get("/programs").then((r) => setPrograms(r.data));
    api.get("/packages").then((r) => setPackages(r.data));
    api.get("/instructors").then((r) => setInstructors(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (user) {
      api.get("/parent/dashboard").then((r) => {
        setChildren(r.data.children);
        setCredits(r.data.credits.filter((c) => c.remaining > 0));
        setOtterRem(r.data.otter_remaining || 0);
        if (r.data.children[0]) setChildId(r.data.children[0].id);
      }).catch(() => {});
    }
  }, [user]);

  const pkgScrolledRef = useRef(false);
  useEffect(() => {
    if (pkgScrolledRef.current || params.get("view") !== "packages" || !packages.length) return;
    pkgScrolledRef.current = true;
    goTo("packages");
  }, [packages]);

  useEffect(() => { setDiscountInfo(null); }, [programId]);

  useEffect(() => { setSelDate(""); setSelTime(""); setSlots([]); }, [instructor]);

  const returnedRef = useRef(false);
  useEffect(() => {
    if (returnedRef.current || !ready || !programs.length || !params.get("program")) return;
    if (user && params.get("continue")) {
      returnedRef.current = true;
      setTimeout(() => document.getElementById("step-2")?.scrollIntoView({ behavior: "smooth", block: "start" }), 450);
    } else if (!user) {
      returnedRef.current = true;
      setTimeout(() => document.getElementById("account-gate")?.scrollIntoView({ behavior: "smooth", block: "start" }), 450);
    }
  }, [ready, user, programs, params]);

  const days = useMemo(() => {
    const monday = startOfWeek(addDays(new Date(), weekOffset * 7), { weekStartsOn: 1 });
    return Array.from({ length: 7 }).map((_, i) => addDays(monday, i));
  }, [weekOffset]);

  const loadSlots = (d) => {
    const ds = format(d, "yyyy-MM-dd");
    setSelDate(ds); setSelTime("");
    api.get(`/availability/slots?date=${ds}&instructor=${instructor}&program_id=${programId}`).then((r) => setSlots(r.data.slots));
  };

  const redeemOtter = async () => {
    if (requireAuth()) return;
    if (!otterCode.trim()) return toast.error("Enter your Rec Center code.");
    try {
      const { data } = await api.post("/otter/redeem", { code: otterCode.trim() });
      setOtterRem((n) => n + data.lessons);
      setOtterCode("");
      toast.success(`${data.lessons} Little Otters sessions added!`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const selectedProgram = programs.find((p) => p.id === programId);
  const isOtter = !!selectedProgram?.rec_center;
  const totalRemaining = credits.reduce((a, c) => a + c.remaining, 0);

  const requireAuth = () => {
    if (ready && !user) { navigate("/signin?redirect=/book"); return true; }
    return false;
  };

  const addChild = async (forms) => {
    const { data } = await api.post("/children", {
      name: newChild.name, age: newChild.age, photo_url: newChild.photoUrl, forms,
    });
    setChildren([...children, data]);
    setChildId(data.id);
    setNewChild({ name: "", age: "", photoUrl: "", uploading: false });
    setRegOpen(false);
    toast.success("Child registered & waiver signed.");
  };

  const openReg = () => {
    if (!newChild.name) return toast.error("Please enter your child's name first.");
    setRegOpen(true);
  };

  const payAndBook = async () => {
    if (requireAuth()) return;
    if (!programId) return toast.error("Please choose a program.");
    if (!childId) return toast.error("Please add or select a child.");
    const sel = children.find((c) => c.id === childId);
    if (sel && !sel.waiver_signed) return toast.error("Please complete the registration & waiver for this child first.");
    if (!selDate || !selTime) return toast.error("Please choose a date and time.");
    setLoading(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        kind: "lesson", program_id: programId, child_id: childId, instructor,
        date: selDate, time: selTime, discount_code: discount || null,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); setLoading(false); }
  };

  const bookWithCredit = async () => {
    if (requireAuth()) return;
    if (!programId || !childId || !selDate || !selTime) return toast.error("Please complete all steps.");
    setLoading(true);
    try {
      await api.post("/bookings/use-credit", { program_id: programId, child_id: childId, instructor, date: selDate, time: selTime });
      toast.success("Lesson booked with your package!");
      navigate("/dashboard");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); setLoading(false); }
  };

  const buyPackage = async (pkgId) => {
    if (requireAuth()) return;
    try {
      const { data } = await api.post("/payments/checkout", { kind: "package", package_id: pkgId, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const venmoLesson = async () => {
    if (requireAuth()) return;
    if (!programId) return toast.error("Please choose a program.");
    if (!childId) return toast.error("Please add or select a child.");
    const sel = children.find((c) => c.id === childId);
    if (sel && !sel.waiver_signed) return toast.error("Please complete the registration & waiver for this child first.");
    if (!selDate || !selTime) return toast.error("Please choose a date and time.");
    setLoading(true);
    try {
      const { data } = await api.post("/bookings/venmo", {
        kind: "lesson", program_id: programId, child_id: childId, instructor,
        date: selDate, time: selTime, discount_code: discount || null,
        origin_url: window.location.origin,
      });
      setVenmoInfo({ ...data, title: selectedProgram?.name || "Lesson" });
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const venmoBuyPackage = async (pkgId, label) => {
    if (requireAuth()) return;
    try {
      const { data } = await api.post("/bookings/venmo", { kind: "package", package_id: pkgId, origin_url: window.location.origin });
      setVenmoInfo({ ...data, title: label });
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const venmoGift = async (pkgId, gift, label) => {
    if (requireAuth()) return;
    try {
      const { data } = await api.post("/bookings/venmo", {
        kind: "gift", package_id: pkgId,
        gift_recipient_name: gift.name, gift_recipient_email: gift.email, gift_message: gift.message,
        origin_url: window.location.origin,
      });
      setVenmoInfo({ ...data, title: "Gift: " + label });
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const applyDiscount = async () => {
    if (!discount.trim()) { setDiscountInfo(null); return; }
    if (!programId) return toast.error("Please choose a program first.");
    setApplyingCode(true);
    try {
      const { data } = await api.post("/discounts/preview", { code: discount.trim(), program_id: programId });
      setDiscountInfo(data);
      if (data.valid) toast.success(`Code applied — ${data.description}!`);
      else toast.error(data.message || "Invalid code.");
    } catch (err) {
      setDiscountInfo({ valid: false, message: formatApiError(err.response?.data?.detail) });
    } finally { setApplyingCode(false); }
  };

  const buyGift = async (pkgId, gift) => {
    if (requireAuth()) return;
    try {
      const { data } = await api.post("/payments/checkout", {
        kind: "gift", package_id: pkgId, origin_url: window.location.origin,
        gift_recipient_name: gift.name, gift_recipient_email: gift.email, gift_message: gift.message,
      });
      window.location.href = data.checkout_url;
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div className="bg-cream min-h-screen">
      <Navbar />
      <section className="relative bg-navy py-16 overflow-hidden">
        <Bubbles count={12} dark />
        <div className="relative mx-auto max-w-4xl px-6 text-center">
          <h1 className="font-serif text-4xl sm:text-5xl text-white">Book Your Adventure</h1>
          <p className="mt-3 text-cream/75">Choose a program, pick a time, and set sail toward safer water.</p>
        </div>
      </section>

      <div className="mx-auto max-w-4xl px-6 py-14 space-y-10">
        {showWelcome && user && selectedProgram && (
          <div data-testid="welcome-back" className="flex items-center justify-between gap-4 rounded-2xl border border-teal/30 bg-teal/10 px-5 py-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 grid place-items-center rounded-full bg-teal text-white shrink-0"><Sparkles size={20} /></div>
              <p className="text-navy"><span className="font-serif text-lg">Welcome back{user.name ? ", " + user.name.split(" ")[0] : ""}!</span> <span className="text-navy/70">Let's finish booking your {selectedProgram.name} — just add your child and pick a time below.</span></p>
            </div>
            <button onClick={() => setShowWelcome(false)} data-testid="welcome-dismiss" className="text-navy/40 hover:text-navy transition-colors shrink-0"><X size={18} /></button>
          </div>
        )}
        {/* Step 1: program */}
        <Step n="1" title="Choose your program" icon={Anchor}>
          <div className="grid gap-4 sm:grid-cols-2">
            {programs.map((p) => (
              <button key={p.id} data-testid={`book-program-${p.id}`} onClick={() => { setProgramId(p.id); goTo(user ? "step-2" : "account-gate"); }}
                className={`text-left rounded-2xl border overflow-hidden transition-[transform,box-shadow,border-color] duration-200 ${programId === p.id ? "border-teal ring-2 ring-teal bg-white" : "border-gold/30 bg-white hover:-translate-y-0.5 hover:shadow-lg"}`}>
                {p.image_url && <div className="aspect-[3/4] overflow-hidden bg-gradient-to-b from-navy/10 via-teal/10 to-cream"><img src={p.image_url} alt={p.name} className="h-full w-full object-contain" /></div>}
                <div className="p-5">
                  <div className="flex items-center justify-between">
                    <h3 className="font-serif text-xl text-navy">{p.name}</h3>
                    {programId === p.id && <CheckCircle2 className="text-teal" size={20} />}
                  </div>
                  <div className="mt-2 flex flex-wrap gap-2 text-xs text-navy/70">
                    <span className="inline-flex items-center gap-1 rounded-full bg-lavender/15 px-2.5 py-1"><Users size={12} /> {p.ages}</span>
                    <span className="inline-flex items-center gap-1 rounded-full bg-teal/15 px-2.5 py-1"><Clock size={12} /> {p.length_min} min</span>
                    {p.rec_center && <span className="inline-flex items-center gap-1 rounded-full bg-[#3D95CE]/15 text-[#2E7DB0] px-2.5 py-1"><Anchor size={12} /> Rec Center</span>}
                  </div>
                  {p.rec_center ? (
                    <p className="mt-3 text-coral font-serif text-2xl">${Number(p.price).toFixed(0)} <span className="text-sm text-navy/60 font-sans">/ 8 lessons · pay at Rec Center</span></p>
                  ) : (
                    <p className="mt-3 text-coral font-serif text-2xl">${Number(p.price).toFixed(0)}</p>
                  )}
                </div>
              </button>
            ))}
          </div>
        </Step>

        {!user && <SignInGate programId={programId} navigate={navigate} />}

        {user && (<>
        {/* Step 2: child */}
        <Step n="2" title="Who is swimming?" icon={Users} id="step-2">
          {(
            <>
              {children.length > 0 && (
                <div className="flex flex-wrap gap-3 mb-5">
                  {children.map((c) => (
                    <button key={c.id} data-testid={`book-child-${c.id}`} onClick={() => { setChildId(c.id); goTo("step-3"); }}
                      className={`rounded-full px-5 py-2.5 border transition-colors ${childId === c.id ? "bg-teal text-white border-teal" : "bg-white border-gold/40 text-navy hover:border-teal"}`}>{c.name}{c.waiver_signed ? "" : " ⚠"}</button>
                  ))}
                </div>
              )}
              <div className="rounded-2xl border border-gold/30 bg-white p-5">
                <p className="font-medium text-navy mb-3">Add a child</p>
                <div className="grid sm:grid-cols-2 gap-3">
                  <div><Label>Child's name</Label><Input data-testid="book-newchild-name" value={newChild.name} onChange={(e) => setNewChild({ ...newChild, name: e.target.value })} className="mt-1" /></div>
                  <div><Label>Age</Label><Input data-testid="book-newchild-age" value={newChild.age} onChange={(e) => setNewChild({ ...newChild, age: e.target.value })} className="mt-1" placeholder="e.g. 5" /></div>
                </div>
                <div className="mt-3">
                  <Label>Child's photo (optional)</Label>
                  <div className="mt-1 flex items-center gap-3">
                    {newChild.photoUrl && <img src={newChild.photoUrl} alt="child" className="h-14 w-14 rounded-full object-cover border border-gold/40" />}
                    <label className="cursor-pointer rounded-full border border-navy/30 text-navy text-sm px-4 py-2 hover:bg-navy hover:text-cream transition-colors">
                      {newChild.uploading ? "Uploading…" : newChild.photoUrl ? "Change photo" : "Upload photo"}
                      <input type="file" accept="image/*" className="hidden" data-testid="book-child-photo" onChange={async (e) => {
                        const f = e.target.files?.[0]; if (!f) return;
                        setNewChild((p) => ({ ...p, uploading: true }));
                        try { const url = await uploadImage(f); setNewChild((p) => ({ ...p, photoUrl: url, uploading: false })); }
                        catch (err) { toast.error(formatApiError(err.response?.data?.detail)); setNewChild((p) => ({ ...p, uploading: false })); }
                      }} />
                    </label>
                  </div>
                </div>
                <label className="flex items-start gap-3 mt-4 text-sm text-navy/70">
                  <span>A one-time Registration &amp; Liability Waiver is required before booking. Click below to complete and sign it for this child.</span>
                </label>
                <Button onClick={openReg} data-testid="book-addchild" variant="outline" className="mt-3 rounded-full border-navy text-navy hover:bg-navy hover:text-cream">Complete Registration &amp; Waiver</Button>
                <RegistrationForm open={regOpen} onOpenChange={setRegOpen} childName={newChild.name} mode="edit" onSubmit={addChild} />
              </div>
              {children.length > 0 && children.every((c) => !c.waiver_signed) && (
                <p className="mt-3 text-sm text-coral">Please complete registration &amp; waiver for your child before booking.</p>
              )}
            </>
          )}
        </Step>

        {/* Step 3: time */}
        <Step n="3" title="Pick a time" icon={CalendarDays} id="step-3">
          <div className="mb-5">
            <p className="text-sm font-medium text-navy mb-2">Choose your instructor</p>
            <div className="flex flex-wrap gap-3">
              {instructors.map((ins) => (
                <button key={ins.id} data-testid={`book-instructor-${ins.id}`} onClick={() => setInstructor(ins.id)}
                  className={`rounded-full px-5 py-2.5 border transition-colors ${instructor === ins.id ? "bg-teal text-white border-teal" : "bg-white border-gold/40 text-navy hover:border-teal"}`}>{ins.name}</button>
              ))}
            </div>
          </div>
          <div className="flex items-center justify-between mb-4">
            <Button variant="ghost" disabled={weekOffset === 0} onClick={() => setWeekOffset(weekOffset - 1)} className="text-navy" data-testid="book-week-prev">Previous week</Button>
            <span className="text-sm text-navy/60">{format(days[0], "MMM d")} – {format(days[6], "MMM d")}</span>
            <Button variant="ghost" onClick={() => setWeekOffset(weekOffset + 1)} className="text-navy" data-testid="book-week-next">Next week <ChevronRight size={16} /></Button>
          </div>
          <div className="grid grid-cols-7 gap-2">
            {days.map((d) => {
              const ds = format(d, "yyyy-MM-dd");
              const isPast = d < new Date(new Date().setHours(0, 0, 0, 0));
              return (
                <button key={ds} disabled={isPast} data-testid={`book-day-${ds}`} onClick={() => loadSlots(d)}
                  className={`rounded-xl py-3 text-center border transition-colors ${selDate === ds ? "bg-navy text-cream border-navy" : "bg-white border-gold/30 text-navy hover:border-teal"} ${isPast ? "opacity-30 cursor-not-allowed" : ""}`}>
                  <span className="block text-[10px] uppercase tracking-wide">{format(d, "EEE")}</span>
                  <span className="block font-serif text-lg">{format(d, "d")}</span>
                </button>
              );
            })}
          </div>
          {selDate && (
            <div className="mt-6">
              {slots.length === 0 ? (
                <p className="text-navy/60 text-sm">No open times this day. Try another day or join the waiting list.</p>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {slots.map((t) => (
                    <button key={t} data-testid={`book-slot-${t}`} onClick={() => { setSelTime(t); goTo("step-4"); }}
                      className={`rounded-full px-4 py-2 text-sm border transition-colors ${selTime === t ? "bg-coral text-white border-coral" : "bg-white border-gold/40 text-navy hover:border-coral"}`}>{formatTime(t)}</button>
                  ))}
                </div>
              )}
              <p className="mt-3 text-xs text-navy/50">Times shown in America/Chicago.</p>
            </div>
          )}
        </Step>

        {/* Step 4: checkout */}
        <Step n="4" title="Confirm & pay" icon={Ticket} id="step-4">
          <div className="rounded-2xl border border-gold/30 bg-white p-6">
            <div className="flex justify-between text-navy"><span>Program</span><span className="font-medium">{selectedProgram?.name || "—"}</span></div>
            <div className="flex justify-between text-navy mt-2"><span>Instructor</span><span className="font-medium">{instructors.find((i) => i.id === instructor)?.name || "—"}</span></div>
            <div className="flex justify-between text-navy mt-2"><span>When</span><span className="font-medium">{selDate ? `${format(new Date(selDate.length === 10 ? selDate + "T00:00:00" : selDate), "EEE, MMM d")}${selTime ? " · " + formatTime(selTime) : ""}` : "—"}</span></div>
            <div className="flex justify-between items-center text-navy mt-2">
              <span>Price</span>
              {discountInfo?.valid ? (
                <span className="flex items-baseline gap-2">
                  <span className="text-navy/40 line-through text-base">${discountInfo.original.toFixed(2)}</span>
                  <span className="font-serif text-2xl text-coral">${discountInfo.discounted.toFixed(2)}</span>
                </span>
              ) : (
                <span className="font-serif text-2xl text-coral">${selectedProgram ? Number(selectedProgram.price).toFixed(2) : "0.00"}</span>
              )}
            </div>
            {discountInfo?.valid && <div className="flex justify-between text-teal text-sm mt-1" data-testid="book-savings"><span>Discount ({discountInfo.description})</span><span>−${discountInfo.savings.toFixed(2)}</span></div>}
            {!isOtter && <p className="text-xs text-navy/40 mt-1">Tax calculated at checkout. Card details are handled securely by Stripe and never stored on this site.</p>}
            {!isOtter && (<div className="mt-4">
              <Label>Discount code (optional)</Label>
              <div className="mt-1 flex gap-2 max-w-sm">
                <Input data-testid="book-discount" value={discount} onChange={(e) => { setDiscount(e.target.value.toUpperCase()); setDiscountInfo(null); }} onKeyDown={(e) => e.key === "Enter" && applyDiscount()} placeholder="Enter code" />
                <Button type="button" variant="outline" onClick={applyDiscount} disabled={applyingCode} data-testid="book-apply-discount" className="rounded-full border-navy text-navy hover:bg-navy hover:text-cream shrink-0">{applyingCode ? "Applying…" : "Apply"}</Button>
              </div>
              {discountInfo && !discountInfo.valid && <p className="mt-1.5 text-sm text-coral" data-testid="book-discount-error">{discountInfo.message}</p>}
              {discountInfo?.valid && <p className="mt-1.5 text-sm text-teal" data-testid="book-discount-ok">✓ {discountInfo.description} applied — you save ${discountInfo.savings.toFixed(2)}.</p>}
            </div>)}
            {isOtter && (
              <div className="mt-6 rounded-2xl bg-[#3D95CE]/10 border border-[#3D95CE]/30 p-5" data-testid="otter-block">
                <p className="text-navy font-medium">Rec Center program — no payment on this site</p>
                <p className="mt-1 text-sm text-navy/70">Register and pay for Little Otters at the North Platte Rec Center. Enter the code they gave you to add your 8 sessions, then reserve your Mon/Wed/Fri 9:00–12:00 times here.</p>
                <div className="mt-3 flex gap-2 max-w-sm">
                  <Input data-testid="otter-code" value={otterCode} onChange={(e) => setOtterCode(e.target.value.toUpperCase())} placeholder="Rec Center code" />
                  <Button type="button" variant="outline" onClick={redeemOtter} data-testid="otter-redeem" className="rounded-full border-[#3D95CE] text-[#3D95CE] hover:bg-[#3D95CE] hover:text-white shrink-0">Add sessions</Button>
                </div>
                <p className="mt-2 text-sm text-[#2E7DB0]" data-testid="otter-remaining">{otterRem > 0 ? `${otterRem} Little Otters session${otterRem === 1 ? "" : "s"} available` : "No sessions yet — enter your code above."}</p>
                <Button onClick={bookWithCredit} disabled={loading || otterRem < 1} data-testid="otter-reserve" className="mt-4 rounded-full bg-[#3D95CE] hover:bg-[#2E7DB0] text-white px-8 py-6 text-base disabled:opacity-40">{loading ? "Reserving…" : "Reserve this session"}</Button>
              </div>
            )}
            {!isOtter && (<div className="mt-6">
              <p className="text-sm font-medium text-navy mb-2">Payment method</p>
              <div className="flex flex-wrap gap-2 mb-4">
                <button onClick={() => setPayMethod("card")} data-testid="paymethod-card"
                  className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm border transition-colors ${payMethod === "card" ? "bg-navy text-cream border-navy" : "bg-white border-gold/40 text-navy hover:border-teal"}`}>
                  <Ticket size={15} /> Pay by card now
                </button>
                <button onClick={() => setPayMethod("venmo")} data-testid="paymethod-venmo"
                  className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm border transition-colors ${payMethod === "venmo" ? "bg-[#3D95CE] text-white border-[#3D95CE]" : "bg-white border-gold/40 text-navy hover:border-[#3D95CE]"}`}>
                  <Wallet size={15} /> Pay with Venmo
                </button>
              </div>
              <div className="flex flex-wrap gap-3">
                {payMethod === "card" ? (
                  <Button onClick={payAndBook} disabled={loading} data-testid="book-pay" className="rounded-full bg-coral hover:bg-[#C95B50] text-white px-8 py-6 text-base">
                    {loading ? "Redirecting…" : "Pay & Book"}
                  </Button>
                ) : (
                  <Button onClick={venmoLesson} disabled={loading} data-testid="book-venmo" className="rounded-full bg-[#3D95CE] hover:bg-[#2E7DB0] text-white px-8 py-6 text-base">
                    <Wallet size={16} className="mr-2" />{loading ? "Reserving…" : "Reserve & Pay with Venmo"}
                  </Button>
                )}
                {user && totalRemaining > 0 && (
                  <Button onClick={bookWithCredit} disabled={loading} data-testid="book-credit" variant="outline" className="rounded-full border-teal text-teal hover:bg-teal hover:text-white px-8 py-6 text-base">
                    Use package lesson ({totalRemaining} left)
                  </Button>
                )}
              </div>
              {payMethod === "venmo" && <p className="mt-3 text-xs text-navy/50">Your spot is reserved right away and confirmed once we receive your Venmo payment.</p>}
            </div>)}
          </div>
        </Step>
        </>)}

        {/* Packages */}
        <div id="packages" className="rounded-3xl bg-navy text-cream p-8 relative overflow-hidden scroll-mt-24">
          <div className="absolute inset-0 fn-scales opacity-30" />
          <div className="relative">
            <h2 className="font-serif text-3xl text-white">Save with a lesson package</h2>
            <p className="mt-2 text-cream/70">Buy multiple lessons up front and book them whenever it suits your family.</p>
            <div className="mt-6 grid sm:grid-cols-2 gap-4">
              {packages.map((pk) => (
                <div key={pk.id} className="rounded-2xl bg-white/10 border border-gold/30 p-5">
                  <h3 className="font-serif text-xl text-white">{pk.name}</h3>
                  <p className="text-cream/70 text-sm mt-1">{pk.description}</p>
                  <div className="mt-4 flex items-center justify-between gap-2">
                    <span className="font-serif text-2xl text-gold">${Number(pk.price).toFixed(0)}</span>
                    <div className="flex gap-2">
                      <GiftDialog pkg={pk} onBuy={buyGift} onVenmo={(id, g) => venmoGift(id, g, pk.name)} />
                      <Button onClick={() => venmoBuyPackage(pk.id, pk.name)} data-testid={`venmo-package-${pk.id}`} variant="outline" className="rounded-full border-cream/40 text-cream hover:bg-[#3D95CE] hover:border-[#3D95CE] bg-transparent"><Wallet size={16} className="mr-1" />Venmo</Button>
                      <Button onClick={() => buyPackage(pk.id)} data-testid={`buy-package-${pk.id}`} className="rounded-full bg-coral hover:bg-[#C95B50] text-white">Buy</Button>
                    </div>
                  </div>
                  <p className="mt-2 text-[11px] text-cream/50">Perfect as a gift for friends &amp; grandkids.</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      <VenmoDialog info={venmoInfo} onClose={() => { setVenmoInfo(null); navigate("/dashboard"); }} />
      <Footer />
    </div>
  );
}

function GiftDialog({ pkg, onBuy, onVenmo }) {
  const [open, setOpen] = useState(false);
  const [gift, setGift] = useState({ name: "", email: "", message: "" });
  const submit = () => {
    if (!gift.email) return toast.error("Enter the recipient's email.");
    onBuy(pkg.id, gift);
  };
  const submitVenmo = () => {
    if (!gift.email) return toast.error("Enter the recipient's email.");
    setOpen(false);
    onVenmo(pkg.id, gift);
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" data-testid={`gift-package-${pkg.id}`} className="rounded-full border-gold/50 text-gold hover:bg-gold hover:text-navy bg-transparent"><Gift size={16} className="mr-1" />Gift</Button>
      </DialogTrigger>
      <DialogContent className="bg-cream">
        <DialogHeader><DialogTitle className="font-serif text-2xl text-navy">Gift {pkg.name}</DialogTitle></DialogHeader>
        <p className="text-navy/70 text-sm">Give the gift of water confidence. We'll email a redemption code to your recipient after payment.</p>
        <div className="space-y-3 mt-2">
          <div><Label>Recipient's name</Label><Input data-testid="gift-name" value={gift.name} onChange={(e) => setGift({ ...gift, name: e.target.value })} className="mt-1 bg-white" /></div>
          <div><Label>Recipient's email</Label><Input type="email" data-testid="gift-email" value={gift.email} onChange={(e) => setGift({ ...gift, email: e.target.value })} className="mt-1 bg-white" /></div>
          <div><Label>Personal message (optional)</Label><Textarea data-testid="gift-message" value={gift.message} onChange={(e) => setGift({ ...gift, message: e.target.value })} className="mt-1 bg-white" /></div>
        </div>
        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button onClick={submitVenmo} data-testid="gift-venmo" variant="outline" className="rounded-full border-[#3D95CE] text-[#3D95CE] hover:bg-[#3D95CE] hover:text-white"><Wallet size={16} className="mr-2" />Pay with Venmo</Button>
          <Button onClick={submit} data-testid="gift-checkout" className="rounded-full bg-coral hover:bg-[#C95B50] text-white"><Gift size={16} className="mr-2" />Pay by card · ${Number(pkg.price).toFixed(0)}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function VenmoDialog({ info, onClose }) {
  const username = (info?.venmo_handle || "").replace(/^@/, "").trim();
  const note = encodeURIComponent(`${info?.title || "Float Now"} — Float Now`);
  const amount = Number(info?.amount || 0).toFixed(2);
  const payLink = username
    ? `https://venmo.com/${username}?txn=pay&amount=${amount}&note=${note}`
    : (info?.venmo_link || "");
  return (
    <Dialog open={!!info} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="bg-cream" data-testid="venmo-dialog">
        <DialogHeader><DialogTitle className="font-serif text-2xl text-navy flex items-center gap-2"><Wallet size={22} className="text-[#3D95CE]" /> Pay with Venmo</DialogTitle></DialogHeader>
        <p className="text-navy/75 text-sm">
          {info?.kind === "lesson" ? "Your lesson is reserved" : "Your order is reserved"}! To confirm, send{" "}
          <strong className="text-navy">${amount}</strong> on Venmo to:
        </p>
        <div className="my-2 rounded-2xl bg-[#3D95CE]/10 border border-[#3D95CE]/30 p-4 text-center">
          <p className="font-serif text-2xl text-[#2E7DB0]" data-testid="venmo-handle">{info?.venmo_handle || "Ask your instructor"}</p>
          {payLink && (
            <a href={payLink} target="_blank" rel="noreferrer" data-testid="venmo-pay-link"
              className="mt-4 inline-flex items-center justify-center gap-2 rounded-full bg-[#3D95CE] hover:bg-[#2E7DB0] text-white px-6 py-3 text-sm font-medium transition-colors">
              <Wallet size={16} /> Pay ${amount} in Venmo
            </a>
          )}
          {info?.venmo_qr_url && (
            <div className="mt-4">
              <p className="text-xs text-navy/50 mb-1">or scan to pay</p>
              <img src={info.venmo_qr_url} alt="Venmo QR code" data-testid="venmo-qr" className="mx-auto h-40 w-40 rounded-xl bg-white p-2 border border-[#3D95CE]/20" />
            </div>
          )}
        </div>
        <p className="text-navy/70 text-sm">{info?.venmo_instructions}</p>
        <DialogFooter>
          <Button onClick={onClose} data-testid="venmo-done" className="rounded-full bg-[#3D95CE] hover:bg-[#2E7DB0] text-white">Done — go to my dashboard</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SignInGate({ programId, navigate }) {
  const target = "/signin?redirect=" + encodeURIComponent("/book" + (programId ? "?program=" + programId + "&continue=1" : "?continue=1"));
  return (
    <div id="account-gate" className="scroll-mt-24 rounded-3xl border border-teal/30 bg-white p-8 text-center shadow-[0_8px_32px_rgba(15,32,39,0.05)]" data-testid="signin-gate">
      <div className="mx-auto h-14 w-14 grid place-items-center rounded-full bg-teal/10 text-teal mb-4"><Anchor size={26} /></div>
      <h2 className="font-serif text-2xl text-navy">{programId ? "Great choice! Sign in to continue" : "Sign in to book"}</h2>
      <p className="mt-2 text-navy/70 max-w-md mx-auto">Create your free parent account (or sign in) to add your child, pick a time, and finish booking. It only takes a moment.</p>
      <div className="mt-6">
        <Button onClick={() => navigate(target)} data-testid="gate-signin" className="rounded-full bg-coral hover:bg-[#C95B50] text-white px-8 py-6 text-base">Sign in or create account</Button>
      </div>
    </div>
  );
}

function Step({ n, title, icon: Icon, children, id }) {
  return (
    <div id={id} className="scroll-mt-24">
      <div className="flex items-center gap-3 mb-5">
        <div className="h-10 w-10 grid place-items-center rounded-full bg-teal text-white font-serif">{n}</div>
        <h2 className="font-serif text-2xl text-navy flex items-center gap-2"><Icon size={20} className="text-teal" /> {title}</h2>
      </div>
      {children}
    </div>
  );
}

function formatTime(t) {
  const [h, m] = t.split(":").map(Number);
  const ampm = h >= 12 ? "PM" : "AM";
  const hh = h % 12 || 12;
  return `${hh}:${String(m).padStart(2, "0")} ${ampm}`;
}
